import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
  IconButton,
  Tooltip,
  Chip,
  CircularProgress
} from '@mui/material';
import React, { useState, useEffect } from 'react';
import { SelectLanguageComponent } from './SelectLanguageComponent';
import { useVideoProcessing } from '../../contexts/VideoProcessingContext';
import videoUploadService from '../../services/VideoUploadService';
import { validateItemName } from '../../utils/formatUtils';
import FolderIcon from '@mui/icons-material/Folder';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import PathSelectorDialog from '../dialogs/PathSelectorDialog';
import axios from 'axios';

const UploadVideoDialog = ({
  fileName,
  onFileSelect,
  linkType,
  handleLinkTypeSelect,
  setLinkError,
  open,
  handleClose,
  videoName,
  setVideoName,
  googleLinkURL,
  setGoogleLinkURL,
  youtubeLinkURL,
  setYoutubeLinkURL,
  isLinkError,
  getLanguagesForCreateVideo,
  isCompressed,
  setIsCompressed,
  selectOpenAI,
  setSelectOpenAI,
  isLogging,
  setIsLogging,
  skipTranslationIsChecked,
  setSkipTranslationIsChecked,
  onlyTransliterationIsChecked,
  setOnlyTransliterationIsChecked,
  bitRate,
  setBitRate,
  crfValue,
  setCRFValue,
  uploadVideo,
  setOpen,
  folderPath,
  darkMode, 
  themeVariant,
}) => {
  const [isFileSelected, setIsFileSelected] = useState(false);
  const [videoNameError, setVideoNameError] = useState(null);
  const [pathSelectorOpen, setPathSelectorOpen] = useState(false);
  const [selectedPath, setSelectedPath] = useState(folderPath || '/');
  
  // New states for drive link validation
  const [checkingDriveLink, setCheckingDriveLink] = useState(false);
  const [driveLinkVerified, setDriveLinkVerified] = useState(false);
  const [driveLinkErrorMessage, setDriveLinkErrorMessage] = useState('');
  
  // Add this state to track visibility of developer options
  const [showDeveloperOptions, setShowDeveloperOptions] = useState(false);
  
  // Get the tracking function from context
  const { trackVideo } = useVideoProcessing();
  
  useEffect(() => {
    //console.log("Setting up video tracking function");
    videoUploadService.setTrackingFunction(trackVideo);
    
    // Initialize path from props when dialog opens
    if (open && folderPath) {
      setSelectedPath(folderPath);
    }
    
    return () => {
      videoUploadService.setTrackingFunction(null);
    };
  }, [trackVideo, open, folderPath]);

  // Reset drive link verification when URL changes
  useEffect(() => {
    if (googleLinkURL) {
      setDriveLinkVerified(false);
      setDriveLinkErrorMessage('');
    }
  }, [googleLinkURL]);

  // Validate name when component mounts or videoName prop changes
  useEffect(() => {
    if (videoName) {
      const error = validateItemName(videoName, 'video');
      setVideoNameError(error);
    }
  }, [videoName]);

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    setIsFileSelected(!!file);

    if (file) {
      onFileSelect(file);
      handleLinkTypeSelect('local');
    }
  };

  const handleVideoUpload = async () => {
    const nameError = validateItemName(videoName, 'video');
    
    if (nameError) {
      setVideoNameError(nameError);
      return;
    }
    
    // If name is valid, trim any extra spaces before uploading
    const trimmedName = videoName.trim();
    if (trimmedName !== videoName) {
      setVideoName(trimmedName);
    }
    
    // If it's a Google Drive link and not verified, verify it first
    if (linkType === 'gdrive' && !driveLinkVerified) {
      const isAccessible = await verifyDriveLink();
      if (!isAccessible) {
        return;
      }
    }
    
    // Call the upload function from props with the selected path
    return uploadVideo(linkType, selectedPath);
  };
  
  const handlePathSelected = (path) => {
    setSelectedPath(path);
    setPathSelectorOpen(false);
  };
  
  const formatPath = (path) => {
    if (!path) return 'Home';
    if (path === '/') return 'Home';
    return path;
  };

  // Function to verify Google Drive link accessibility
  const verifyDriveLink = async () => {
    if (!googleLinkURL.trim()) {
      setDriveLinkErrorMessage('Please enter a Google Drive link');
      setLinkError(true);
      return false;
    }

    setCheckingDriveLink(true);
    
    try {
      // Simple HEAD request to check if the drive link is accessible
      const response = await axios.head(googleLinkURL, {
        timeout: 5000 // 5-second timeout
      });
      
      // If we get a 200 response, the link is accessible
      if (response.status === 200) {
        setDriveLinkVerified(true);
        setLinkError(false);
        setDriveLinkErrorMessage('');
        return true;
      } else {
        throw new Error('Link returned non-200 status');
      }
    } catch (error) {
      console.error('Error verifying drive link:', error);
      setDriveLinkVerified(false);
      setLinkError(true);
      setDriveLinkErrorMessage(
        'This link appears to be restricted. Please make sure it\'s publicly accessible.'
      );
      return false;
    } finally {
      setCheckingDriveLink(false);
    }
  };

  // Generate section title with divider
  const SectionTitle = ({ children }) => (
    <Box sx={{ mt: 3, mb: 2, position: 'relative' }}>
      <Divider>
        <Typography 
          variant="subtitle1" 
          component="span" 
          sx={{ 
            px: 1.5, 
            fontWeight: 500,
            bgcolor: 'background.paper' 
          }}
        >
          {children}
        </Typography>
      </Divider>
    </Box>
  );
  
  return (
    <>
      <Dialog 
        open={open} 
        onClose={handleClose}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
        data-testid="upload-video-dialog"
      >
        <DialogTitle>
          <Typography variant="h6" component="div" data-testid="upload-video-dialog-title">
            Upload Video
          </Typography>
        </DialogTitle>

        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Please complete the form and click Upload to begin the processing.
          </Typography>
          
          {/* Video Name field with validation */}
          <TextField
            id="videoNameInput"
            label="Video Name"
            variant="outlined"
            fullWidth
            margin="normal"
            value={videoName}
            onChange={(e) => {
              const newName = e.target.value;
              setVideoName(newName);
              
              const error = validateItemName(newName,'video');
              setVideoNameError(error);
            }}
            onBlur={() => {
              const error = validateItemName(videoName, 'video');
              setVideoNameError(error);
            }}
            required
            error={!!videoNameError}
            helperText={videoNameError || ''}
            data-testid="video-name-input"
          />
          
          {/* Destination Path Selector */}
          <Box sx={{ mt: 2, mb: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Destination Folder:
            </Typography>
            <Paper 
              variant="outlined" 
              sx={{ 
                p: 1.5, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                borderRadius: 1
              }}
              data-testid="destination-folder-display"
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <FolderIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="body2" sx={{ fontWeight: 500 }} data-testid="selected-path-display">
                  {formatPath(selectedPath)}
                </Typography>
              </Box>
              <Tooltip title="Change destination folder">
                <IconButton 
                  size="small" 
                  color="primary" 
                  onClick={() => setPathSelectorOpen(true)}
                  aria-label="Change folder"
                  data-testid="change-destination-btn"
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Paper>
          </Box>

          {/* Video Source Options */}
          <Paper variant="outlined" sx={{ p: 2, mt: 2, borderRadius: 1 }} data-testid="video-source-options">
            <Grid container spacing={2} direction="column">
              {/* Google Drive Option with verification */}
              <Grid item>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <TextField
                    id="videoDriveURLInput"
                    label="Google Drive Link"
                    variant="outlined"
                    fullWidth
                    value={googleLinkURL}
                    onChange={(e) => {
                      setGoogleLinkURL(e.target.value);
                      handleLinkTypeSelect('gdrive');
                      setLinkError(false);
                      setDriveLinkVerified(false);
                    }}
                    onBlur={(e) => {
                      // Get the current value directly from the event
                      const currentValue = e.target.value;
                      
                      // Only verify if there's actually a value and it's not already verified
                      if (currentValue && currentValue.trim() !== '' && !driveLinkVerified && !checkingDriveLink) {
                        // Use a ref to store the timeout ID
                        const timeoutId = setTimeout(() => {
                          verifyDriveLink();
                        }, 300);
                        
                        // Clean up timeout if component unmounts
                        return () => clearTimeout(timeoutId);
                      }
                    }}
                    error={isLinkError}
                    InputProps={{
                      endAdornment: driveLinkVerified ? (
                        <CheckCircleIcon color="success" sx={{ mr: 1 }} />
                      ) : null
                    }}
                    data-testid="google-drive-input"
                  />
                  <Button
                    variant="outlined"
                    color="primary"
                    disabled={!googleLinkURL || checkingDriveLink}
                    onClick={verifyDriveLink}
                    sx={{ minWidth: '120px', height: '56px' }}
                    data-testid="verify-drive-link-btn"
                  >
                    {checkingDriveLink ? (
                      <CircularProgress size={24} />
                    ) : (
                      'Check Access'
                    )}
                  </Button>
                </Box>
                {googleLinkURL && driveLinkErrorMessage && (
                  <Typography 
                    variant="caption" 
                    color="error" 
                    sx={{ display: 'block', mt: 0.5 }}
                    data-testid="drive-link-error"
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <ErrorIcon fontSize="small" />
                      {driveLinkErrorMessage}
                    </Box>
                  </Typography>
                )}
                {driveLinkVerified && (
                  <Typography 
                    variant="caption" 
                    color="success.main" 
                    sx={{ display: 'block', mt: 0.5 }}
                    data-testid="drive-link-success"
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <CheckCircleIcon fontSize="small" />
                      Link is accessible and ready for processing
                    </Box>
                  </Typography>
                )}
              </Grid>
              
              <Grid item>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Divider sx={{ flexGrow: 1 }} />
                  <Typography variant="body2" color="text.secondary" sx={{ px: 1 }}>or</Typography>
                  <Divider sx={{ flexGrow: 1 }} />
                </Box>
              </Grid>
              
              {/* YouTube Option */}
              <Grid item>
                <TextField
                  id="youtubeURLInput"
                  label="YouTube Link"
                  variant="outlined"
                  fullWidth
                  placeholder="Coming soon - not fully functional right now"
                  value={youtubeLinkURL}
                  onChange={(e) => {
                    setYoutubeLinkURL(e.target.value);
                    handleLinkTypeSelect('youtube');
                    setLinkError(false);
                  }}
                  data-testid="youtube-input"
                />
              </Grid>
              
              <Grid item>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Divider sx={{ flexGrow: 1 }} />
                  <Typography variant="body2" color="text.secondary" sx={{ px: 1 }}>or</Typography>
                  <Divider sx={{ flexGrow: 1 }} />
                </Box>
              </Grid>
              
              {/* Local File Option */}
              <Grid item>
                <Button
                  variant="outlined"
                  component="label"
                  fullWidth
                  sx={{ py: 1.5, textTransform: 'none' }}
                  data-testid="local-file-upload-btn"
                >
                  {isFileSelected ? 'Change Selected File' : 'Upload Local File'}
                  <input
                    id="localFileInput"
                    type="file"
                    accept="video/*"
                    hidden
                    onChange={handleFileChange}
                    data-testid="local-file-input"
                  />
                </Button>
                {isFileSelected && (
                  <Typography 
                    variant="caption" 
                    color="text.secondary" 
                    sx={{ display: 'block', mt: 1 }}
                    data-testid="selected-file-name"
                  >
                    Selected: {fileName?.name || "File selected"}
                  </Typography>
                )}
              </Grid>
            </Grid>
          </Paper>

          {isLinkError && !driveLinkErrorMessage && (
            <Typography variant="body2" color="error" sx={{ mt: 1 }} data-testid="link-error-message">
              The link is not publicly accessible.
            </Typography>
          )}
          
          {/* Language Selection */}
          <SectionTitle>Language Selection</SectionTitle>
          <Box sx={{ my: 2 }} data-testid="language-selection-container">
            <SelectLanguageComponent
              id="selectLanguageDropdown"
              disabled={false}
              languageOptions={getLanguagesForCreateVideo}
              darkMode={darkMode}
              themeVariant={themeVariant}
              data-testid="language-dropdown"
            />
          </Box>
          
          {/* Developer Tools */}
          <SectionTitle>Developer Tools</SectionTitle>
            

          <Grid container spacing={2} sx={{ mt: 1 }} data-testid="developer-tools-container">
            {/* Basic options - always visible, now in a single row */}
            <Grid item xs={12} >
              <Box sx={{ display: 'flex', flexDirection: 'row', gap: 6 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      id="skipTranslationIsChecked"
                      checked={skipTranslationIsChecked}
                      onChange={(e) => setSkipTranslationIsChecked(e.target.checked)}
                      data-testid="skip-translation-checkbox"
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Typography variant="body2">Skip Translation</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                        Will generate an empty SRT file
                      </Typography>
                    </Box>
                  }
                />
                
                <FormControlLabel
                  control={
                    <Checkbox
                      id="onlyTransliterationIsChecked"
                      checked={onlyTransliterationIsChecked}
                      onChange={(e) => setOnlyTransliterationIsChecked(e.target.checked)}
                      data-testid="only-transliteration-checkbox"
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Typography variant="body2">Only Transliteration</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                        Will generate Gujarati SRT file, and an empty English SRT file
                      </Typography>
                    </Box>
                  }
                />

                <Button 
                  size="small" 
                  onClick={() => setShowDeveloperOptions(!showDeveloperOptions)}
                  sx={{ ml: 4, mb: 2, textTransform: 'none' }}
                  data-testid="toggle-developer-options"
                >
                  {showDeveloperOptions ? 'Hide Advanced Options' : 'Show Advanced Options'}
                </Button>
              </Box>
            </Grid>
            
            {/* Advanced options - only visible when showDeveloperOptions is true */}
            {showDeveloperOptions && (
              <Grid item xs={12}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <FormGroup>
                      <FormControlLabel
                        control={
                          <Checkbox
                            id="model"
                            checked={selectOpenAI}
                            onChange={(e) => setSelectOpenAI(e.target.checked)}
                            data-testid="enable-openai-checkbox"
                          />
                        }
                        label="Enable Open AI"
                      />
                      
                      <FormControlLabel
                        control={
                          <Checkbox
                            id="isLogging"
                            checked={isLogging}
                            onChange={(e) => setIsLogging(e.target.checked)}
                            data-testid="enable-logging-checkbox"
                          />
                        }
                        label="Enable additional logging"
                      />

                      <FormControlLabel
                        control={
                          <Checkbox
                            id="compressed"
                            checked={isCompressed}
                            onChange={(e) => setIsCompressed(e.target.checked)}
                            data-testid="compressed-checkbox"
                          />
                        }
                        label="Video was compressed before uploading to Google drive"
                      />
                    </FormGroup>
                  </Grid>
                  
                </Grid>
              </Grid>
            )}
          </Grid>

          {/* Video Quality Settings - Only shown when developer options are enabled */}
          {showDeveloperOptions && (
            <Grid container spacing={3} sx={{ mt: 1 }} data-testid="video-quality-settings">
              <Grid item xs={12} md={6}>
                <FormControl fullWidth variant="outlined" size="small">
                  <InputLabel id="bit-rate-label">Bit Rate (kbps)</InputLabel>
                  <Select
                    labelId="bit-rate-label"
                    id="bitRate"
                    value={bitRate}
                    onChange={(e) => setBitRate(e.target.value)}
                    label="Bit Rate (kbps)"
                    data-testid="bit-rate-select"
                  >
                    <MenuItem value="10" data-testid="bit-rate-10">10</MenuItem>
                    <MenuItem value="25" data-testid="bit-rate-25">25</MenuItem>
                    <MenuItem value="50" data-testid="bit-rate-50">50</MenuItem>
                    <MenuItem value="75" data-testid="bit-rate-75">75</MenuItem>
                    <MenuItem value="100" data-testid="bit-rate-100">100</MenuItem>
                    <MenuItem value="115" data-testid="bit-rate-115">115</MenuItem>
                    <MenuItem value="200" data-testid="bit-rate-200">200</MenuItem>
                  </Select>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                    Higher values have better video quality
                  </Typography>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControl fullWidth variant="outlined" size="small">
                  <InputLabel id="crf-label">CRF Value</InputLabel>
                  <Select
                    labelId="crf-label"
                    id="crfValue"
                    value={crfValue}
                    onChange={(e) => setCRFValue(e.target.value)}
                    label="CRF Value"
                    data-testid="crf-value-select"
                  >
                    <MenuItem value="28" data-testid="crf-28">28</MenuItem>
                    <MenuItem value="30" data-testid="crf-30">30</MenuItem>
                    <MenuItem value="32" data-testid="crf-32">32</MenuItem>
                    <MenuItem value="34" data-testid="crf-34">34</MenuItem>
                    <MenuItem value="36" data-testid="crf-36">36</MenuItem>
                    <MenuItem value="38" data-testid="crf-38">38</MenuItem>
                    <MenuItem value="40" data-testid="crf-40">40</MenuItem>
                  </Select>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                    Lower values have better video quality
                  </Typography>
                </FormControl>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        
        <DialogActions sx={{ p: 2, pt: 1.5 }}>
          <Button onClick={handleClose} color="inherit" data-testid="cancel-upload-btn">
            Cancel
          </Button>
          <Button
            id="upLoadButton"
            variant="outlined" 
            color="primary"
            disabled={
              (googleLinkURL === '' && youtubeLinkURL === '' && !isFileSelected) ||
              videoName === '' ||
              !!videoNameError ||
              (linkType === 'gdrive' && !driveLinkVerified) ||
              isLinkError
            }
            onClick={() => {
              const result = handleVideoUpload();
              if (!videoNameError) {
                setOpen(false);
              }
            }}
            data-testid="confirm-upload-btn"
          >
            Upload
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Path selector dialog */}
      <PathSelectorDialog
        open={pathSelectorOpen}
        onClose={() => setPathSelectorOpen(false)}
        onSelect={handlePathSelected}
        currentPath={selectedPath}
        title="Select Destination Folder"
        data-testid="path-selector-dialog"
      />
    </>
  );
};

export default UploadVideoDialog;
